import { useState, useMemo } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Button } from "@/components/ui/button";
import { Check, CaretUpDown } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { COUNTRIES } from "@/lib/locations";

/**
 * Searchable country selector. Replaces the 251-item native <Select> dropdown
 * which was unusable on mobile (auto-positioned off-screen). Uses a Popover +
 * cmdk Command for instant filter-as-you-type. Defaults to MX.
 */
export default function CountrySelect({ value, onChange, placeholder = "Selecciona país", className = "", testid = "country-select", disabled = false }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(() => COUNTRIES.find((c) => c.code === value), [value]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          data-testid={testid}
          className={cn(
            "w-full justify-between bg-[#0A0A0A] border-white/20 text-white h-11 hover:bg-[#0A0A0A] hover:text-white hover:border-[#CCFF00]/40 disabled:opacity-50",
            !selected && "text-gray-500 font-normal",
            className,
          )}
        >
          <span className="truncate">{selected ? selected.name : placeholder}</span>
          <CaretUpDown size={14} className="ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-[#0F0F0F] border-white/15" align="start">
        <Command className="bg-transparent">
          <CommandInput placeholder="Buscar país..." className="h-10 text-sm" data-testid={`${testid}-search`} />
          <CommandList className="max-h-[260px]">
            <CommandEmpty>Sin resultados.</CommandEmpty>
            <CommandGroup>
              {COUNTRIES.map((c) => (
                <CommandItem
                  key={c.code}
                  value={`${c.name} ${c.code}`}
                  onSelect={() => { onChange?.(c.code); setOpen(false); }}
                  data-testid={`${testid}-option-${c.code}`}
                  className="cursor-pointer"
                >
                  <Check
                    size={14}
                    className={cn("mr-2 text-[#CCFF00]", value === c.code ? "opacity-100" : "opacity-0")}
                  />
                  {c.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
